import { Button } from "@/components/ui/button";
import { ButtonGroup, ButtonGroupText } from "@/components/ui/button-group";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useStreamdownTranslations } from "@/hooks/useStreamdownTranslations";
import { useTranslation } from "@/hooks/useTranslation";
import { transformAppLinks } from "@/lib/app-links";
import { collectCompanyMentions } from "@/lib/company-mention-nodes";
import { transformCitationMarkers } from "@/lib/citation-markers";
import { transformCompanyMarkers } from "@/lib/company-mentions";
import { cn } from "@/lib/utils";
import type { FileUIPart, UIMessage } from "ai";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Maximize2Icon,
  PaperclipIcon,
  XIcon,
} from "lucide-react";
import type { ComponentProps, ErrorInfo, ReactElement, ReactNode } from "react";
import {
  Component,
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Streamdown,
  TableCopyDropdown,
  TableDownloadDropdown,
  type AllowedTags,
  type Components,
  type ExtraProps,
} from "streamdown";
import { CitationPill } from "./citation-pill";
import { CompanyMention } from "./company-mention";
import { MarkdownAppLink } from "./markdown-app-link";
import { MarkdownTableListActions } from "./markdown-table-list-actions";

export type MessageProps = ComponentProps<"div"> & {
  from: UIMessage["role"];
};

export function Message({ className, from, ...props }: MessageProps) {
  return (
    <div
      className={cn(
        "group flex w-full max-w-[95%] flex-col gap-2",
        from === "user" ? "is-user ml-auto justify-end" : "is-assistant",
        className
      )}
      data-slot="message"
      {...props}
    />
  );
}

export type MessageContentProps = ComponentProps<"div">;

export function MessageContent({
  children,
  className,
  ...props
}: MessageContentProps) {
  return (
    <div
      className={cn(
        "flex w-fit max-w-full min-w-0 flex-col gap-2 overflow-hidden ai-html-content",
        "group-[.is-user]:ml-auto group-[.is-user]:rounded-lg group-[.is-user]:bg-secondary group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-foreground",
        "group-[.is-assistant]:text-foreground group-[.is-assistant]:w-full",
        className
      )}
      data-slot="message-content"
      {...props}
    >
      {children}
    </div>
  );
}

export type MessageActionsProps = ComponentProps<"div">;

export function MessageActions({
  className,
  children,
  ...props
}: MessageActionsProps) {
  return (
    <div
      className={cn("flex gap-1 items-center", className)}
      data-slot="message-actions"
      {...props}
    >
      {children}
    </div>
  );
}

export type MessageActionProps = ComponentProps<typeof Button> & {
  tooltip?: string;
  label?: string;
};

export function MessageAction({
  tooltip,
  children,
  label,
  variant = "ghost",
  size = "icon",
  ...props
}: MessageActionProps) {
  const button = (
    <Button
      data-slot="message-action"
      size={size}
      type="button"
      variant={variant}
      {...props}
    >
      {children}
      <span className="sr-only">{label || tooltip}</span>
    </Button>
  );

  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
}

type MessageBranchContextType = {
  currentBranch: number;
  totalBranches: number;
  goToPrevious: () => void;
  goToNext: () => void;
  branches: ReactElement[];
  setBranches: (branches: ReactElement[]) => void;
};

const MessageBranchContext = createContext<MessageBranchContextType | null>(
  null
);

const useMessageBranch = () => {
  const context = useContext(MessageBranchContext);

  if (!context) {
    throw new Error(
      "MessageBranch components must be used within MessageBranch"
    );
  }

  return context;
};

export type MessageBranchProps = ComponentProps<"div"> & {
  defaultBranch?: number;
  onBranchChange?: (branchIndex: number) => void;
};

export function MessageBranch({
  defaultBranch = 0,
  onBranchChange,
  className,
  ...props
}: MessageBranchProps) {
  const [currentBranch, setCurrentBranch] = useState(defaultBranch);
  const [branches, setBranches] = useState<ReactElement[]>([]);

  const handleBranchChange = (newBranch: number) => {
    setCurrentBranch(newBranch);
    onBranchChange?.(newBranch);
  };

  const goToPrevious = () => {
    const newBranch =
      currentBranch > 0 ? currentBranch - 1 : branches.length - 1;
    handleBranchChange(newBranch);
  };

  const goToNext = () => {
    const newBranch =
      currentBranch < branches.length - 1 ? currentBranch + 1 : 0;
    handleBranchChange(newBranch);
  };

  const contextValue: MessageBranchContextType = {
    currentBranch,
    totalBranches: branches.length,
    goToPrevious,
    goToNext,
    branches,
    setBranches,
  };

  return (
    <MessageBranchContext.Provider value={contextValue}>
      <div
        className={cn("grid w-full gap-2 [&>div]:pb-0", className)}
        data-slot="message-branch"
        {...props}
      />
    </MessageBranchContext.Provider>
  );
}

export type MessageBranchContentProps = ComponentProps<"div">;

export function MessageBranchContent({
  children,
  ...props
}: MessageBranchContentProps) {
  const { currentBranch, setBranches } = useMessageBranch();

  const branchCount = Array.isArray(children) ? children.length : 1;

  // Recompute only when branch count changes — prevents the effect from firing on every render
  // and causing an update cascade (React Error #185).
  const childrenArray = useMemo(
    () =>
      Array.isArray(children)
        ? (children as ReactElement[])
        : [children as ReactElement],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [branchCount],
  );

  useEffect(() => {
    setBranches(childrenArray);
  }, [childrenArray, setBranches]);

  return childrenArray.map((branch, index) => (
    <div
      className={cn(
        "grid gap-2 overflow-hidden [&>div]:pb-0",
        index === currentBranch ? "block" : "hidden"
      )}
      data-slot="message-branch-content"
      key={branch.key}
      {...props}
    >
      {branch}
    </div>
  ));
}

export type MessageBranchSelectorProps = ComponentProps<"div"> & {
  from: UIMessage["role"];
};

export function MessageBranchSelector({
  className,
  from,
  ...props
}: MessageBranchSelectorProps) {
  const { totalBranches } = useMessageBranch();

  // Don't render if there's only one branch
  if (totalBranches <= 1) {
    return null;
  }

  return (
    <ButtonGroup
      className="[&>*:not(:first-child)]:rounded-l-md [&>*:not(:last-child)]:rounded-r-md"
      data-slot="message-branch-selector"
      orientation="horizontal"
      {...props}
    />
  );
}

export type MessageBranchPreviousProps = ComponentProps<typeof Button>;

export function MessageBranchPrevious({
  children,
  ...props
}: MessageBranchPreviousProps) {
  const { goToPrevious, totalBranches } = useMessageBranch();

  return (
    <Button
      aria-label="Previous branch"
      data-slot="message-branch-previous"
      disabled={totalBranches <= 1}
      onClick={goToPrevious}
      size="icon"
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? <ChevronLeftIcon size={14} />}
    </Button>
  );
}

export type MessageBranchNextProps = ComponentProps<typeof Button>;

export function MessageBranchNext({
  children,
  className,
  ...props
}: MessageBranchNextProps) {
  const { goToNext, totalBranches } = useMessageBranch();

  return (
    <Button
      aria-label="Next branch"
      data-slot="message-branch-next"
      disabled={totalBranches <= 1}
      onClick={goToNext}
      size="icon"
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? <ChevronRightIcon size={14} />}
    </Button>
  );
}

export type MessageBranchPageProps = ComponentProps<"div">;

export function MessageBranchPage({
  className,
  ...props
}: MessageBranchPageProps) {
  const { currentBranch, totalBranches } = useMessageBranch();

  return (
    <ButtonGroupText
      className={cn(
        "bg-transparent border-none shadow-none text-muted-foreground",
        className
      )}
      data-slot="message-branch-page"
      {...props}
    >
      {currentBranch + 1} of {totalBranches}
    </ButtonGroupText>
  );
}

/**
 * Renders raw markdown text as plain wrapped text. Used as a fallback when
 * `Streamdown`'s syntax-highlighting chunk crashes (e.g. React #185:
 * "Maximum update depth exceeded" — the highlighter occasionally enters
 * an infinite setState loop in passive effects). The user still sees the
 * message content, just unstyled — much better than a blank chat.
 */
type StreamdownFallbackProps = {
  fallbackContent: ReactNode;
  className?: string;
};

class StreamdownErrorBoundary extends Component<
  StreamdownFallbackProps & { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
     
    console.warn(
      "[Streamdown] Render error — falling back to raw markdown.",
      error,
      info.componentStack
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className={cn(
            "whitespace-pre-wrap wrap-break-word",
            this.props.className
          )}
        >
          {this.props.fallbackContent}
        </div>
      );
    }
    return this.props.children;
  }
}

// Whether the markdown being rendered is still streaming. Read by the table so
// its actions only appear on a complete set of rows; `animated` is the streaming
// flag every caller already passes down.
const MarkdownStreamingContext = createContext(false);

// Opens the table in a fullscreen dialog, mirroring Streamdown's native
// fullscreen control (which is lost when the default table is overridden).
function MarkdownTableFullscreen({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  return (
    <Dialog>
      <DialogTrigger
        title={t("components.markdown.viewFullscreen")}
        className="p-1 transition-all cursor-pointer text-muted-foreground hover:text-foreground"
      >
        <Maximize2Icon size={14} />
      </DialogTrigger>
      <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-hidden p-4">
        <DialogTitle className="sr-only">
          {t("components.markdown.table")}
        </DialogTitle>
        <div className="overflow-auto rounded-xl border bg-background max-h-[calc(90vh-2rem)]">
          <Table>{children}</Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Render markdown tables with the app's shared `Table` styling (rounded border,
// muted uppercase header) instead of Streamdown's default table. The copy and
// download controls are Streamdown's own — they locate the table through the
// `data-streamdown="table-wrapper"` ancestor — so chat tables keep their
// original actions while matching the DataTable components used elsewhere.
function MarkdownTable({
  node: _node,
  className,
  children,
  ...props
}: ComponentProps<"table"> & ExtraProps) {
  const isStreaming = useContext(MarkdownStreamingContext);
  const tableRef = useRef<HTMLDivElement>(null);
  // A table of companies can be turned into a listing — but only once the rows
  // have all arrived, since a mid-stream table is still growing.
  const companies = useMemo(
    () =>
      isStreaming
        ? { mentions: [], unidentified: [] }
        : collectCompanyMentions(children, CompanyMention),
    [isStreaming, children]
  );

  return (
    <div data-streamdown="table-wrapper" className="my-4 space-y-2">
      <div className="flex gap-1 justify-end items-center">
        <TableCopyDropdown />
        <TableDownloadDropdown />
        <MarkdownTableFullscreen>{children}</MarkdownTableFullscreen>
      </div>
      <div
        ref={tableRef}
        className="overflow-hidden rounded-xl border bg-background"
      >
        <Table className={className} {...props}>
          {children}
        </Table>
      </div>
      <MarkdownTableListActions companies={companies} tableRef={tableRef} />
    </div>
  );
}

const markdownTableComponents: Components = {
  table: MarkdownTable,
  thead: ({ node: _node, ...props }) => <TableHeader {...props} />,
  tbody: ({ node: _node, ...props }) => <TableBody {...props} />,
  tr: ({ node: _node, ...props }) => <TableRow {...props} />,
  th: ({ node: _node, ...props }) => <TableHead {...props} />,
  td: ({ node: _node, ...props }) => <TableCell {...props} />,
};

export type MessageResponseProps = ComponentProps<typeof Streamdown>;

// Inline company mentions: the assistant emits `[@shaarp/company ...]` markers
// that `transformCompanyMarkers` rewrites to `<company>` tags, which Streamdown
// renders through `CompanyMention`. Links to app routes go through the same
// custom-tag path (`<applink>` → `MarkdownAppLink`), which leaves genuine
// external links on Streamdown's default anchor. Declared at module scope for
// referential stability across renders.
const MARKDOWN_ALLOWED_TAGS: AllowedTags = {
  company: [
    "id",
    "name",
    "domain",
    "siren",
    "siret",
    "linkedin",
    "city",
    "address",
    "activity",
    "source",
    "brief",
  ],
  applink: ["href"],
  citation: ["ids"],
};
const MARKDOWN_LITERAL_TAG_CONTENT: string[] = [
  "company",
  "applink",
  "citation",
];
const MARKDOWN_COMPONENTS: Components = {
  company: CompanyMention as Components["company"],
  applink: MarkdownAppLink as Components["applink"],
  citation: CitationPill as Components["citation"],
};

export const MessageResponse = memo(
  ({
    className,
    animated,
    components,
    children,
    ...props
  }: MessageResponseProps) => {
    const translations = useStreamdownTranslations();
    const content =
      typeof children === "string"
        ? transformCitationMarkers(
            transformAppLinks(transformCompanyMarkers(children)),
          )
        : children;

    return (
      <StreamdownErrorBoundary className={className} fallbackContent={children}>
        <MarkdownStreamingContext.Provider value={animated === true}>
          <Streamdown
            className={cn(
              "ai-html-content size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
              "[&_pre]:whitespace-pre-wrap [&_pre]:wrap-break-word [&_pre]:overflow-x-auto",
              className
            )}
            animated={animated === true ? { animation: "blurIn" } : animated}
            allowedTags={MARKDOWN_ALLOWED_TAGS}
            literalTagContent={MARKDOWN_LITERAL_TAG_CONTENT}
            translations={translations}
            components={{
              ...markdownTableComponents,
              ...MARKDOWN_COMPONENTS,
              ...components,
            }}
            {...props}
          >
            {content}
          </Streamdown>
        </MarkdownStreamingContext.Provider>
      </StreamdownErrorBoundary>
    );
  },
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.animated === nextProps.animated
);

MessageResponse.displayName = "MessageResponse";

export type MessageAttachmentProps = ComponentProps<"div"> & {
  data: FileUIPart;
  className?: string;
  onRemove?: () => void;
};

export function MessageAttachment({
  data,
  className,
  onRemove,
  ...props
}: MessageAttachmentProps) {
  const filename = data.filename || "";
  const mediaType =
    data.mediaType?.startsWith("image/") && data.url ? "image" : "file";
  const isImage = mediaType === "image";
  const attachmentLabel = filename || (isImage ? "Image" : "Attachment");

  return (
    <div
      className={cn(
        "overflow-hidden relative rounded-lg group size-24",
        className
      )}
      data-slot="message-attachment"
      {...props}
    >
      {isImage ? (
        <>
          <img
            alt={filename || "attachment"}
            className="object-cover size-full"
            height={100}
            src={data.url}
            width={100}
          />
          {onRemove && (
            <Button
              aria-label="Remove attachment"
              className="absolute top-2 right-2 size-6 rounded-full bg-background/80 p-0 opacity-0 backdrop-blur-xs transition-opacity hover:bg-background group-hover:opacity-100 touch:opacity-100 [&>svg]:size-3"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              type="button"
              variant="ghost"
            >
              <XIcon />
              <span className="sr-only">Remove</span>
            </Button>
          )}
        </>
      ) : (
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex justify-center items-center rounded-lg size-full shrink-0 bg-muted text-muted-foreground">
                <PaperclipIcon className="size-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{attachmentLabel}</p>
            </TooltipContent>
          </Tooltip>
          {onRemove && (
            <Button
              aria-label="Remove attachment"
              className="size-6 shrink-0 rounded-full p-0 opacity-0 transition-opacity hover:bg-accent group-hover:opacity-100 touch:opacity-100 [&>svg]:size-3"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              type="button"
              variant="ghost"
            >
              <XIcon />
              <span className="sr-only">Remove</span>
            </Button>
          )}
        </>
      )}
    </div>
  );
}

export type MessageAttachmentsProps = ComponentProps<"div">;

export function MessageAttachments({
  children,
  className,
  ...props
}: MessageAttachmentsProps) {
  if (!children) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-wrap gap-2 items-start ml-auto w-fit",
        className
      )}
      data-slot="message-attachments"
      {...props}
    >
      {children}
    </div>
  );
}

export type MessageToolbarProps = ComponentProps<"div">;

export function MessageToolbar({
  className,
  children,
  ...props
}: MessageToolbarProps) {
  return (
    <div
      className={cn(
        "flex gap-4 justify-between items-center mt-4 w-full",
        className
      )}
      data-slot="message-toolbar"
      {...props}
    >
      {children}
    </div>
  );
}
